import React, { useCallback, useEffect, useState } from 'react';

import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { useContextHeaderVisibility } from '@fastlessjs/backoffice-layer/dist/context';
import { currentUser } from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Col, Row, Button } from 'antd';

import { ReturnRequestTable } from './ReturnRequestTable';
import { t } from '../../config/translations';
import { useOrder } from '../../orders/hooks';
import OrderNotFound from '../../orders/views/OrderNotFound';
import SearchOrder, { FormData } from '../../orders/views/SearchOrder';
import ViewOrder from '../../orders/views/ViewOrder';
import { useSuiviOrder } from '../../trace/hooks';

// Register this component for debug tracking
debug.registerComponent('ReturnsShow');

const Show = ({ org }: PageProps) => {
  debug.trackRender('ReturnsShow');
  debug.log('ReturnsShow', `Rendering with org: ${org}`);
  const toggleHeaderVisibility = useContextHeaderVisibility();

  useEffect(() => {
    toggleHeaderVisibility?.('enterprise-selector', false);
    return () => {
      toggleHeaderVisibility?.('enterprise-selector', true);
    };
  }, []);

  const [search, setSearch] = useState<FormData | undefined>();

  const handleSubmit = useCallback((orderId: string, clientId?: string) => {
    debug.log(
      'ReturnsShow',
      `Search submitted - orderId: ${orderId}, clientId: ${clientId}`,
    );
    setSearch({ clientId, orderId });
  }, []);

  const handleReset = () => {
    debug.log('ReturnsShow', `Search reset`);
    setSearch(undefined);
  };

  const {
    data: suivi,
    isLoading: isSuiviLoading,
    error: suiviError,
  } = useSuiviOrder({
    tenantId: currentUser()?.user?.id || '',
    orderId: search?.orderId,
    clientId: search?.clientId,
  });

  const {
    data: orderData,
    isLoading: isOrderLoading,
    error: orderError,
  } = useOrder({
    clientId: search?.clientId,
    orderId: search?.orderId,
  });

  // Check if we have any error (client or order not found)
  const hasError = Boolean(suiviError || orderError);
  const isLoading = isSuiviLoading || isOrderLoading;
  const hasSearched = Boolean(search?.clientId && search?.orderId);

  debug.log(
    'ReturnsShow',
    `State - hasError: ${hasError}, isLoading: ${isLoading}, hasSearched: ${hasSearched}`,
  );

  // Show error page when we have an error and finished loading
  const showErrorPage = hasSearched && hasError && !isLoading;

  // Show results only when we have data and no errors
  const showResults = hasSearched && orderData && !hasError;

  // Determine error type for better messaging
  const getErrorType = (): 'client' | 'order' | 'generic' => {
    const errorMessage =
      (suiviError as any)?.message || (orderError as any)?.message || '';
    if (errorMessage.toLowerCase().includes('client')) return 'client';
    if (errorMessage.toLowerCase().includes('order')) return 'order';
    return 'generic';
  };

  // No search yet - show search form
  if (!search) {
    return (
      <Row justify="center" style={{ marginTop: '12px' }} gutter={24}>
        <Col xs={24}>
          <SearchOrder onSubmit={handleSubmit} />
        </Col>
      </Row>
    );
  }

  // Error state - show friendly error page
  if (showErrorPage) {
    return (
      <OrderNotFound
        clientId={search?.clientId}
        orderId={search?.orderId}
        onRetry={handleReset}
        errorType={getErrorType()}
      />
    );
  }

  // Loading or showing results
  return (
    <>
      <div style={{ marginBottom: '16px', textAlign: 'right' }}>
        <Button type="primary" onClick={handleReset} size="large">
          {t('searchModal.searchAnOrder')}
        </Button>
      </div>

      {showResults && (
        <ReturnRequestTable
          clientId={search.clientId!}
          orderId={search.orderId!}
          hasTitle
        />
      )}

      <Row justify="start" gutter={24} wrap>
        <Col xs={24}>
          <ViewOrder
            clientId={search?.clientId}
            orderId={search?.orderId}
            org={org}
            suiviId={suivi?.suiviId}
            isSuiviLoading={isSuiviLoading || isOrderLoading}
          />
        </Col>
      </Row>
    </>
  );
};

export default Show;

// Mark this component as initialized
debug.markInitialized('ReturnsShow');
